import { useMemo } from 'react';
import {
  Box, Button, HStack, Icon, Badge, Text,
  Select, Input, Menu, MenuButton, MenuList, MenuItem, IconButton,
} from '@chakra-ui/react';
import { FiActivity, FiArrowLeft, FiDownload, FiEye, FiTrash2, FiMoreVertical, FiFilter } from 'react-icons/fi';
import type { ColumnDef } from '@tanstack/react-table';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { DataTable } from '@/design-system/components/DataTable';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { ProfileType, ProfileStatus, type Profile } from '@/lib/sdk/profiling/types';
import { useProfileListPage } from '../hooks';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

export function ProfileListPage() {
  const p = useProfileListPage();

  if (!p.isSuperAdmin) {
    return (
      <PageContainer title="Access Denied">
        <Box textAlign="center" py={12}>
          <Icon as={FiActivity} boxSize={16} color="red.500" mb={4} />
          <Text color="text-primary">Super Admin Access Required</Text>
        </Box>
      </PageContainer>
    );
  }

  const columns: ColumnDef<Profile>[] = useMemo(() => [
    {
      accessorKey: 'profile_type', header: 'Type',
      cell: ({ row }) => <Badge colorScheme="purple">{row.original.profile_type.toUpperCase()}</Badge>,
    },
    {
      accessorKey: 'status', header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        return <Badge colorScheme={status === ProfileStatus.COMPLETED ? 'green' : status === ProfileStatus.COLLECTING ? 'blue' : status === ProfileStatus.FAILED ? 'red' : 'gray'}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'duration_seconds', header: 'Duration',
      cell: ({ row }) => <Text color="text-primary" fontSize="sm">{row.original.duration_seconds}s</Text>,
    },
    {
      accessorKey: 'file_size_bytes', header: 'Size',
      cell: ({ row }) => <Text color="text-primary" fontSize="sm">{formatBytes(row.original.file_size_bytes)}</Text>,
    },
    {
      accessorKey: 'description', header: 'Description',
      cell: ({ row }) => <Text color="text-secondary" fontSize="sm" maxW="200px" isTruncated>{row.original.description || '-'}</Text>,
    },
    {
      accessorKey: 'created_at', header: 'Created',
      cell: ({ row }) => <Text color="text-muted" fontSize="sm">{new Date(row.original.created_at).toLocaleString()}</Text>,
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <Menu>
          <MenuButton as={IconButton} icon={<FiMoreVertical />} variant="ghost" size="sm" aria-label="Actions" />
          <MenuList>
            <MenuItem icon={<FiEye />} onClick={() => p.navigate(`/dashboard/${p.orgId}/profiling/profiles/${row.original.id}`)}>View Details</MenuItem>
            {row.original.download_url && <MenuItem as="a" href={row.original.download_url} target="_blank" icon={<FiDownload />}>Download</MenuItem>}
            <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => p.handleDeleteClick(row.original)}>Delete</MenuItem>
          </MenuList>
        </Menu>
      ),
    },
  ], [p.navigate, p.orgId, p.handleDeleteClick]);

  return (
    <PageContainer
      title="All Profiles"
      description="View and manage collected performance profiles"
      action={
        <Button leftIcon={<FiArrowLeft />} variant="outline" onClick={() => p.navigate(`/dashboard/${p.orgId}/profiling`)} borderColor="border-default" _hover={{ borderColor: 'border-hover' }}>
          Back to Dashboard
        </Button>
      }
    >
      <Box p={4} borderWidth="1px" borderColor="border-default" borderRadius="lg" bg="bg-surface" mb={4}>
        <HStack spacing={4} wrap="wrap">
          <HStack><Icon as={FiFilter} color="text-muted" /><Text color="text-secondary" fontSize="sm">Filters:</Text></HStack>
          <Select size="sm" maxW="200px" placeholder="All Types" value={p.filters.profile_type || ''} onChange={(e) => p.setFilters({ ...p.filters, profile_type: e.target.value as ProfileType || undefined, offset: 0 })} bg="bg-surface" borderColor="border-default">
            {Object.values(ProfileType).map(type => <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>)}
          </Select>
          <Input size="sm" type="date" maxW="180px" value={p.filters.from_date || ''} onChange={(e) => p.setFilters({ ...p.filters, from_date: e.target.value || undefined, offset: 0 })} bg="bg-surface" borderColor="border-default" />
          <Input size="sm" type="date" maxW="180px" value={p.filters.to_date || ''} onChange={(e) => p.setFilters({ ...p.filters, to_date: e.target.value || undefined, offset: 0 })} bg="bg-surface" borderColor="border-default" />
          {(p.filters.profile_type || p.filters.from_date || p.filters.to_date) && (
            <Button size="sm" variant="ghost" onClick={() => p.setFilters({ limit: 20, offset: 0 })}>Clear Filters</Button>
          )}
        </HStack>
      </Box>

      <DataTable
        data={p.profilesData?.items || []}
        columns={columns}
        isLoading={p.isLoading}
        emptyMessage="No profiles found"
        enableSorting enablePagination pageSize={20}
        onRowClick={(profile) => p.navigate(`/dashboard/${p.orgId}/profiling/profiles/${profile.id}`)}
      />

      <ConfirmDialog
        isOpen={p.isDeleteOpen}
        onClose={() => { p.onDeleteClose(); p.setDeletingProfile(null); }}
        onConfirm={p.handleConfirmDelete}
        title="Delete Profile"
        message={`Are you sure you want to delete this ${p.deletingProfile?.profile_type.toUpperCase()} profile? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={p.isDeleting}
      />
    </PageContainer>
  );
}
